import React, { useState, useEffect } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { supabase } from "../../../supabaseClient";
import { API_URL } from "../../../globals";
import Alert from "@mui/material/Alert";
import { CircularProgress, IconButton } from "@mui/material";
import axios from "axios";
import { Save, RotateCcw, FileWarning, Check } from 'lucide-react';
import FumeConfigForm from "../../../components/FumeConfigForm";

const CodeEditor = ({ fileName, onCodeChange, studioId }) => {
  const [value, setValue] = useState("");
  const [initialValue, setInitialValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [isModified, setIsModified] = useState(false);
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const truncateFromStart = (str, maxWidth) => {
    if (str.length <= maxWidth) return str;
    return '...' + str.slice(-(maxWidth - 3));
  };

  const handleCodeChange = (newValue) => {
    setValue(newValue);
    onCodeChange(newValue);
  };

  const getFileContent = async () => {
    setLoading(true);
    const authToken = (await supabase.auth.getSession()).data.session
      .access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };
    try {
      const response = await axios.get(
        `${API_URL}/studio/get-file?studioId=${studioId}&filePath=${fileName}`,
        {
          headers: headers,
        }
      );
      if (response.status !== 200) {
        setBannerMessage("Error loading file");
        return;
      }
      if (!response.data) {
        setValue("");
        setInitialValue("");
        setBannerMessage("File is empty");
        return;
      }

      const fileContent =
        typeof response.data === "string"
          ? response.data
          : JSON.stringify(response.data, null, 2);

      setValue(fileContent);
      setInitialValue(fileContent);
      setBannerMessage("");
    } catch (error) {
      setBannerMessage("Error loading file");
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getLanguageExtension = (fileName) => {
    const extension = fileName.split(".").pop();
    switch (extension) {
      case "js":
        return javascript();
      case "py":
        return python();
      case "html":
        return html();
      case "css":
        return css();
      default:
        return [];
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const authToken = (await supabase.auth.getSession()).data.session
      .access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };
    try {
      const response = await axios.post(
        `${API_URL}/studio/save-file`,
        {
          studio_id: studioId,
          file_path: fileName,
          content: value,
        },
        {
          headers: headers,
        }
      );
      if (response.status !== 200) {
        setBannerMessage("Error saving file");
        return;
      }
      setSaveSuccessful(true);
      setIsModified(false);
      setInitialValue(value);
      setBannerMessage("");
      setTimeout(() => setSaveSuccessful(false), 3000);
    } catch (error) {
      setBannerMessage("Error saving file");
      console.log(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevert = () => {
    setValue(initialValue);
    setIsModified(false);
  };

  useEffect(() => {
    if (fileName) {
      getFileContent();
    }
  }, [fileName]);

  useEffect(() => {
    setIsModified(value !== initialValue);
  }, [value, initialValue]);

  return (
    <div
      style={{
        borderRadius: "4px",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "center",
        position: "relative",
        backgroundColor: '#121212',
      }}
    >
      {loading && (
        <CircularProgress style={{ color: "#fff", margin: "auto" }} />
      )}
      {!loading && (
        <>
          {fileName && (
            <div
              style={{
                backgroundColor: "transparent",
                padding: "8px",
                fontFamily: "monospace",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "12px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#1E1E1E",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  maxWidth: "500px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  direction: "rtl",
                  textAlign: "left",
                }}
                title={fileName}
              >
                {truncateFromStart(fileName, 80)}
              </div>
              <div>
                {isModified && (
                  <>
                    <IconButton
                      onClick={handleSave}
                      style={{ color: "#fff", padding: "4px" }}
                      disabled={isSaving}
                      size="small"
                    >
                      {isSaving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Save size={16} />
                      )}
                    </IconButton>
                    <IconButton
                      onClick={handleRevert}
                      style={{ color: "#fff", padding: "4px" }}
                      disabled={isSaving}
                      size="small"
                    >
                      <RotateCcw size={16} />
                    </IconButton>
                  </>
                )}
              </div>
            </div>
          )}
          {fileName == "./.fumeconfig" ? (
            <FumeConfigForm studioId={studioId} initialState={value} />
          ) : (
            <CodeMirror
              value={value}
              theme="dark"
              height="90vh"
              extensions={getLanguageExtension(fileName)}
              onChange={handleCodeChange}
              style={{ fontSize: '13px' }}
              options={{
                theme: "dark",
                indentWithTab: true,
                smartIndent: true,
                lineWiseCopyCut: true,
                rectangularSelection: true,
              }}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                highlightSpecialChars: true,
                history: true,
                foldGutter: true,
                drawSelection: true,
                dropCursor: true,
                allowMultipleSelections: true,
                indentOnInput: true,
                syntaxHighlighting: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
                rectangularSelection: true,
                crosshairCursor: true,
                highlightActiveLine: true,
                highlightSelectionMatches: true,
                closeBracketsKeymap: true,
                defaultKeymap: true,
                searchKeymap: true,
                historyKeymap: true,
                foldKeymap: true,
                completionKeymap: true,
                lintKeymap: true,
              }}
            />
          )}
        </>
      )}
      {!loading && (bannerMessage || saveSuccessful) && (
        <div
          style={{
            position: "absolute",
            top: "8px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1,
          }}
        >
          {bannerMessage && (
            <Alert
              icon={<FileWarning size={16} />}
              severity="info"
              style={{ 
                textAlign: "center",
                fontSize: "12px",
                padding: "4px 8px",
                borderRadius: "4px",
                backgroundColor: "#1E1E1E",
              }}
            >
              {bannerMessage}
            </Alert>
          )}
          {saveSuccessful && (
            <Alert
              icon={<Check size={16} />}
              severity="success"
              style={{ 
                textAlign: "center",
                fontSize: "12px",
                padding: "4px 8px",
                borderRadius: "4px",
                backgroundColor: "#1E1E1E",
              }}
            >
              File saved successfully
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default CodeEditor;
